/**
 * 考核周期管理弹窗
 */

import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Tag, Space, Form, Input, Select, DatePicker, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { PerformancePeriod, performancePeriodService } from '../../../services/administrationService';

interface PeriodConfigModalProps {
    visible: boolean;
    onCancel: () => void;
}

const PeriodConfigModal: React.FC<PeriodConfigModalProps> = ({ visible, onCancel }) => {
    const [periods, setPeriods] = useState<PerformancePeriod[]>([]);
    const [loading, setLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form] = Form.useForm();

    const loadPeriods = async () => {
        setLoading(true);
        try {
            const data = await performancePeriodService.list();
            setPeriods(data);
        } catch (error) {
            message.error('加载列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) loadPeriods();
    }, [visible]);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setEditModalVisible(true);
    };

    const handleEdit = (record: PerformancePeriod) => {
        setEditingId(record.id);
        form.setFieldsValue({
            ...record,
            range: [dayjs(record.start_date), dayjs(record.end_date)]
        });
        setEditModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await performancePeriodService.delete(id);
            message.success('删除成功');
            loadPeriods();
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                name: values.name,
                period_type: values.period_type,
                start_date: values.range[0].format('YYYY-MM-DD'),
                end_date: values.range[1].format('YYYY-MM-DD'),
                remarks: values.remarks
            };

            if (editingId) {
                await performancePeriodService.update(editingId, payload);
            } else {
                await performancePeriodService.create(payload);
            }
            message.success('保存成功');
            setEditModalVisible(false);
            loadPeriods();
        } catch (error) {
            console.error(error);
            message.error('保存失败');
        }
    };

    const handleActivate = async (id: number) => {
        try {
            await performancePeriodService.activate(id);
            message.success('已激活');
            loadPeriods();
        } catch (error) {
            message.error('激活失败');
        }
    };

    const handleClosePeriod = async (id: number) => {
        try {
            await performancePeriodService.close(id);
            message.success('已结束');
            loadPeriods();
        } catch (error) {
            message.error('操作失败');
        }
    };

    const columns: ColumnsType<PerformancePeriod> = [
        { title: '名称', dataIndex: 'name', key: 'name' },
        { title: '类型', dataIndex: 'period_type_display', key: 'type', width: 80 },
        {
            title: '时间范围',
            key: 'range',
            width: 200,
            render: (_, r) => `${r.start_date} ~ ${r.end_date}`
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => {
                const colors: Record<string, string> = { draft: 'default', active: 'processing', closed: 'success' };
                return <Tag color={colors[status]}>{record.status_display}</Tag>;
            }
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    {record.status === 'draft' && (
                        <Button type="link" size="small" onClick={() => handleActivate(record.id)}>激活</Button>
                    )}
                    {record.status === 'active' && (
                        <Popconfirm title="确定结束此周期？将计算最终得分" onConfirm={() => handleClosePeriod(record.id)}>
                            <Button type="link" size="small" danger>结束</Button>
                        </Popconfirm>
                    )}
                    <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    {record.status === 'draft' && (
                        <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Modal
            title="考核周期管理"
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={900}
        >
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增周期</Button>
            </div>

            <Table
                columns={columns}
                dataSource={periods}
                rowKey="id"
                size="small"
                loading={loading}
                pagination={{ pageSize: 5 }}
            />

            <Modal
                title={editingId ? "编辑周期" : "新增周期"}
                open={editModalVisible}
                onOk={handleSave}
                onCancel={() => setEditModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="周期名称" rules={[{ required: true }]}>
                        <Input placeholder="例：2024年第一季度考核" />
                    </Form.Item>
                    <Form.Item name="period_type" label="类型" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="monthly">月度</Select.Option>
                            <Select.Option value="quarterly">季度</Select.Option>
                            <Select.Option value="half_year">半年度</Select.Option>
                            <Select.Option value="yearly">年度</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="range" label="起止日期" rules={[{ required: true }]}>
                        <DatePicker.RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="remarks" label="备注">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>
        </Modal>
    );
};

export default PeriodConfigModal;
